import { useEffect, useState } from "react";
import { Link } from "react-router";
import { Spinner } from "@/components/ds";
import { AdminPage } from "@/components/app/admin/AdminPage";
import { usePageTitle } from "../hooks/usePageTitle";
import styles from "./AdminHomePage.module.css";

interface OrgSummary {
  readonly total: number;
  readonly trialEnding: number;
  readonly pastDue: number;
  readonly suspended: number;
  readonly totalCleaners: number;
  readonly monthlyEstimateGbp: number;
}

type State =
  | { kind: "loading" }
  | { kind: "unauthorized" }
  | { kind: "ready"; summary: OrgSummary }
  | { kind: "error"; message: string };

export function AdminHomePage() {
  usePageTitle("Admin");
  const [state, setState] = useState<State>({ kind: "loading" });

  useEffect(() => {
    fetch("/api/admin/orgs")
      .then(async (res) => {
        if (res.status === 404) { setState({ kind: "unauthorized" }); return; }
        if (!res.ok) { setState({ kind: "error", message: `Failed (${res.status})` }); return; }
        const json = await res.json() as {
          success: boolean;
          data?: {
            orgs: Array<{
              cleanerCount: number;
              monthlyEstimateGbp: number;
              billingState: { label: string };
            }>;
          };
        };
        if (!json.success || !json.data) {
          setState({ kind: "error", message: "Unexpected response" });
          return;
        }
        const orgs = json.data.orgs;
        setState({
          kind: "ready",
          summary: {
            total: orgs.length,
            trialEnding: orgs.filter((o) => o.billingState.label === "trial_ending").length,
            pastDue: orgs.filter((o) => o.billingState.label === "past_due").length,
            suspended: orgs.filter((o) => o.billingState.label === "suspended").length,
            totalCleaners: orgs.reduce((sum, o) => sum + o.cleanerCount, 0),
            monthlyEstimateGbp: orgs.reduce((sum, o) => sum + o.monthlyEstimateGbp, 0),
          },
        });
      })
      .catch(() => setState({ kind: "error", message: "Network error" }));
  }, []);

  if (state.kind === "unauthorized") {
    return <AdminPage title="Not found" showBack={false}><div /></AdminPage>;
  }
  if (state.kind === "loading") {
    return <AdminPage title="Admin" showBack={false}><Spinner /></AdminPage>;
  }
  if (state.kind === "error") {
    return <AdminPage title="Admin" showBack={false}><div>{state.message}</div></AdminPage>;
  }

  const s = state.summary;
  const flags: string[] = [];
  if (s.trialEnding > 0) flags.push(`${s.trialEnding} trial ending`);
  if (s.pastDue > 0) flags.push(`${s.pastDue} past due`);
  if (s.suspended > 0) flags.push(`${s.suspended} suspended`);

  return (
    <AdminPage title="Admin" showBack={false}>
      <div className={styles.summary}>
        <span><b>{s.total}</b> orgs</span>
        <span><b>{s.totalCleaners}</b> cleaners</span>
        <span><b>£{s.monthlyEstimateGbp.toFixed(2)}</b>/mo est.</span>
      </div>

      <div className={styles.list}>
        <Link to="/admin/orgs" className={styles.link}>
          <span className={styles.linkName}>Organisations</span>
          <span className={styles.linkMeta}>
            {flags.map((f) => (
              <span
                key={f}
                className={f.includes("suspended") ? styles.flagDanger : styles.flagWarn}
              >
                {f}
              </span>
            ))}
            <span>{s.total}</span>
          </span>
        </Link>
        <Link to="/admin/stripe-events" className={styles.link}>
          <span className={styles.linkName}>Stripe events</span>
        </Link>
        <Link to="/admin/billing-audit" className={styles.link}>
          <span className={styles.linkName}>Billing audit</span>
        </Link>
        <Link to="/admin/emails" className={styles.link}>
          <span className={styles.linkName}>Email log</span>
        </Link>
        <Link to="/admin/emails/templates" className={styles.link}>
          <span className={styles.linkName}>Email templates</span>
        </Link>
        <Link to="/admin/funnel" className={styles.link}>
          <span className={styles.linkName}>Funnel</span>
        </Link>
      </div>
    </AdminPage>
  );
}
